export interface ZipEntry {
  filename: string;
  content: string;
}

const encoder = new TextEncoder();
const UTF8_FLAG = 0x0800;
const STORE_METHOD = 0;

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let index = 0; index < table.length; index += 1) {
    let value = index;
    for (let bit = 0; bit < 8; bit += 1) value = (value & 1) ? (0xedb88320 ^ (value >>> 1)) : (value >>> 1);
    table[index] = value >>> 0;
  }
  return table;
})();

export function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of bytes) crc = (CRC_TABLE[(crc ^ byte) & 0xff] ?? 0) ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

function dosTimestamp(date: Date): { time: number; date: number } {
  const year = Math.max(1980, Math.min(2107, date.getUTCFullYear()));
  return {
    time: (date.getUTCHours() << 11) | (date.getUTCMinutes() << 5) | Math.floor(date.getUTCSeconds() / 2),
    date: ((year - 1980) << 9) | ((date.getUTCMonth() + 1) << 5) | date.getUTCDate(),
  };
}

function concatenate(parts: Uint8Array[]): Uint8Array {
  const result = new Uint8Array(parts.reduce((total, part) => total + part.length, 0));
  let offset = 0;
  for (const part of parts) {
    result.set(part, offset);
    offset += part.length;
  }
  return result;
}

function assertClassicZipLimit(value: number, label: string, maximum = 0xffffffff): void {
  if (!Number.isSafeInteger(value) || value < 0 || value > maximum) throw new Error(`${label} is too large for this ZIP export.`);
}

export function createZip(entries: ZipEntry[], modifiedAt = new Date()): Uint8Array {
  assertClassicZipLimit(entries.length, "The file count", 0xffff);
  const localParts: Uint8Array[] = [];
  const centralParts: Uint8Array[] = [];
  const timestamp = dosTimestamp(modifiedAt);
  let localOffset = 0;

  for (const entry of entries) {
    const filename = encoder.encode(entry.filename);
    const content = encoder.encode(entry.content);
    assertClassicZipLimit(filename.length, `The filename ${entry.filename}`, 0xffff);
    assertClassicZipLimit(content.length, `The file ${entry.filename}`);
    assertClassicZipLimit(localOffset, "The archive");
    const checksum = crc32(content);

    const localHeader = new Uint8Array(30);
    const local = new DataView(localHeader.buffer);
    local.setUint32(0, 0x04034b50, true);
    local.setUint16(4, 20, true);
    local.setUint16(6, UTF8_FLAG, true);
    local.setUint16(8, STORE_METHOD, true);
    local.setUint16(10, timestamp.time, true);
    local.setUint16(12, timestamp.date, true);
    local.setUint32(14, checksum, true);
    local.setUint32(18, content.length, true);
    local.setUint32(22, content.length, true);
    local.setUint16(26, filename.length, true);
    local.setUint16(28, 0, true);
    localParts.push(localHeader, filename, content);

    const centralHeader = new Uint8Array(46);
    const central = new DataView(centralHeader.buffer);
    central.setUint32(0, 0x02014b50, true);
    central.setUint16(4, 20, true);
    central.setUint16(6, 20, true);
    central.setUint16(8, UTF8_FLAG, true);
    central.setUint16(10, STORE_METHOD, true);
    central.setUint16(12, timestamp.time, true);
    central.setUint16(14, timestamp.date, true);
    central.setUint32(16, checksum, true);
    central.setUint32(20, content.length, true);
    central.setUint32(24, content.length, true);
    central.setUint16(28, filename.length, true);
    central.setUint16(30, 0, true);
    central.setUint16(32, 0, true);
    central.setUint16(34, 0, true);
    central.setUint16(36, 0, true);
    central.setUint32(38, 0, true);
    central.setUint32(42, localOffset, true);
    centralParts.push(centralHeader, filename);

    localOffset += localHeader.length + filename.length + content.length;
  }

  const localData = concatenate(localParts);
  const centralData = concatenate(centralParts);
  assertClassicZipLimit(localData.length, "The archive");
  assertClassicZipLimit(centralData.length, "The archive directory");
  const end = new Uint8Array(22);
  const endView = new DataView(end.buffer);
  endView.setUint32(0, 0x06054b50, true);
  endView.setUint16(4, 0, true);
  endView.setUint16(6, 0, true);
  endView.setUint16(8, entries.length, true);
  endView.setUint16(10, entries.length, true);
  endView.setUint32(12, centralData.length, true);
  endView.setUint32(16, localData.length, true);
  endView.setUint16(20, 0, true);
  return concatenate([localData, centralData, end]);
}
